import { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Ban } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../components/ui/Table';
import { userAPI } from '../services/ApiServices';

export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockingReason, setBlockingReason] = useState('');
  const [userId, setUserId] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');


  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetErrors, setResetErrors] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    mobile: '',
    role: 'KYC_OFFICER',
  });


  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await userAPI.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalConfirm = () => {
    setShowSuccessModal(false);
    setShowErrorModal(false);
  };

  const handleOpenModal = (user) => {
    setErrors({}); // clear old errors

    if (user) {
      setEditingUser(user);
      setUserId(user._id);

      setFormData({
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        mobile: user.mobile,
        role: user.role,
      });
    } else {
      setEditingUser(null);

      setFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        mobile: '',
        role: 'KYC_OFFICER',
      });
    }

    setShowModal(true);
  };


  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      mobile: '',
      password: '',
      role: 'KYC_OFFICER',
    });
  };

  // Validation function
  const validateField = (fieldName, value) => {
    let error = '';

    switch (fieldName) {
      case 'firstName':
      case 'lastName':
        if (!value.trim()) {
          error = `${fieldName === 'firstName' ? 'First' : 'Last'} name is required`;
        } else if (value.length < 2) {
          error = `${fieldName === 'firstName' ? 'First' : 'Last'} name must be at least 2 characters`;
        } else if (!/^[A-Za-z\s\-']+$/.test(value)) {
          error = `${fieldName === 'firstName' ? 'First' : 'Last'} name can only contain letters, spaces, hyphens, and apostrophes`;
        }
        break;

      case 'username':
        if (!value.trim()) {
          error = 'Username is required';
        } else if (value.length < 3) {
          error = 'Username must be at least 3 characters';
        } else if (!/^[A-Za-z0-9_]+$/.test(value)) {
          error = 'Username can only contain letters, numbers, and underscores';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'password':
        if (!editingUser) {
          if (!value.trim()) {
            error = 'Password is required';
          } else if (value.length < 8) {
            error = 'Password must be at least 8 characters';
          } else if (
            !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(value)
          ) {
            error =
              'Password must contain uppercase, lowercase, number, and special character';
          }
        }
        break;
      case 'mobile':
        if (!value.trim()) {
          error = 'Mobile number is required';
        } else if (!/^(\+\d{1,3})?\d{10}$/.test(value.replace(/\s/g, ''))) {
          error = 'Please enter a valid 10-digit mobile number';
        }
        break;

      case 'role':
        if (!value) {
          error = 'Role is required';
        }
        break;

      default:
        // No validation for unspecified fields
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return !error;
  };

  // Form submission validation
  const validateForm = () => {
    const fields = ['firstName', 'lastName', 'username', 'email', 'mobile', 'role'];
    if (!editingUser) fields.push('password');

    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    return isValid;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (editingUser) {
        const { password, ...payload } = formData; // 👈 strip password
        await userAPI.updateUser(userId, payload);
        setSuccessMessage('User updated successfully');
      } else {
        await userAPI.createUser(formData);
        setSuccessMessage('User created successfully');
      }

      await fetchUsers();
      handleCloseModal();
      setShowSuccessModal(true);
    } catch (error) {
      setErrorMessage('Operation failed. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleBlockUser = async () => {
    // if (!confirm('Are you sure you want to delete this user?')) {
    //   return;
    // }
    try {
      await userAPI.blockUser(userId, blockingReason);
      await fetchUsers();
      setShowBlockModal(false);
      setSuccessMessage('User blocked successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to block user:', error);
      // alert('Failed to block user');
      setShowBlockModal(false);
      setErrorMessage('Failed to block user');
      setShowErrorModal(true);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setResetErrors('Password is required');
      return;
    }

    if (newPassword.length < 8) {
      setResetErrors('Password must be at least 8 characters');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newPassword)) {
      setResetErrors(
        'Password must contain uppercase, lowercase, number, and special character'
      );
      return;
    }

    try {
      await userAPI.resetPassword(resetUserId, {
        newPassword: newPassword,
      });

      setShowResetModal(false);
      setNewPassword('');
      setSuccessMessage('Password reset successfully');
      setShowSuccessModal(true);

    } catch (error) {

      let message = "Something went wrong.";

      if (error.response?.data) {
        const backend = error.response.data;

        // Your backend structure
        if (backend.error) {
          message = backend.error;
        }

        // Optional: Show required permission
        if (backend.required?.length) {
          message += ` (Required: ${backend.required.join(", ")})`;
        }
      } else if (error.response?.status === 403) {
        message = "You don't have permission to perform this action.";
      } else if (error.response?.status === 401) {
        message = "Session expired. Please login again.";
      }

      setShowResetModal(false);
      setErrorMessage(message);
      setShowErrorModal(true);
    }
  };

  // const handleResetPassword = async (userId) => {
  //   // if (!confirm('Are you sure you want to delete this user?')) {
  //   //   return;
  //   // }

  //   try {
  //     await userAPI.deleteUser(userId);
  //     await fetchUsers();
  //   } catch (error) {
  //     console.error('Failed to delete user:', error);
  //     alert('Failed to delete user');
  //   }
  // };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="modal bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="modal-header mb-4">
                <h3 className="text-xl font-semibold text-green-600">Success!</h3>
              </div>
              <div className="modal-body mb-6">
                <p className="text-gray-700">{successMessage}</p>
              </div>
              <div className="modal-footer flex justify-end">
                <button
                  onClick={handleModalConfirm}
                  className="gap-2 w-10 rounded-sm rounded bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="modal bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="modal-header mb-4">
                <h3 className="text-xl font-semibold text-red-600">Error!</h3>
              </div>
              <div className="modal-body mb-6">
                <p className="text-gray-700">{errorMessage}</p>
              </div>
              <div className="modal-footer flex justify-end">
                <button
                  onClick={handleModalConfirm}
                  className="gap-2 w-10 rounded-sm rounded bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            </div>
            <p className="text-gray-600">Manage system users and permissions</p>
          </div>
          <Button
            onClick={() => handleOpenModal()}
            className="gap-2 bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
          >
            <Plus className="w-5 h-5" />
            Add User
          </Button>
        </div>

        <Card padding="none">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Username</TableHeader>
                <TableHeader>Email</TableHeader>
                <TableHeader>Role</TableHeader>
                <TableHeader>Created At</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#472f92] flex items-center justify-center text-white font-semibold text-sm">
                        {user.firstName.charAt(0).toUpperCase() + user.lastName.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium">{user.firstName} {user.lastName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{user.username}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-600">{user.email}</span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${user.role === 'admin'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                        }`}
                    >
                      {user.role.toUpperCase()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-600">{user.isActive ? 'Active' : 'Inactive'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-600">{formatDate(user.createdAt)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenModal(user)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="block"
                        onClick={() => { setShowBlockModal(true); setUserId(user._id); }}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Ban className="w-4 h-4" />
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setResetUserId(user._id);
                          setShowResetModal(true);
                        }}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        Reset Password
                      </Button>

                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Block User MODAL */}
      <Modal isOpen={showBlockModal} onClose={() => setShowBlockModal(false)}>
        <input
          className="w-full border p-3 rounded"
          placeholder="Blocking reason"
          value={blockingReason}
          onChange={(e) => setBlockingReason(e.target.value)}
        />
        <div className="flex justify-end mt-4 gap-2">
          <Button variant="ghost" onClick={() => setShowBlockModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleBlockUser()}>
            Confirm Block
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingUser ? 'Edit User' : 'Add New User'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* State for validation errors */}
          {submitAttempted && errors && Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {Object.values(errors).filter(error => error).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Column */}
            <div className="space-y-4">
              <div className="min-w-[200px]">
                <Input
                  label="First Name"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, firstName: value });
                    // Validate on change
                    validateField('firstName', value);
                  }}
                  onBlur={(e) => validateField('firstName', e.target.value)}
                  required
                  error={errors.firstName}
                  className={`w-full px-3 py-2 border-2 ${errors.firstName ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm min-w-0`}
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
              </div>

              <div className="min-w-[200px]">
                <Input
                  label="Username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\s/g, ''); // Remove spaces
                    setFormData({ ...formData, username: value });
                    validateField('username', value);
                  }}
                  onBlur={(e) => validateField('username', e.target.value)}
                  required
                  error={errors.username}
                  className={`w-full px-3 py-2 border-2 ${errors.username ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm min-w-0`}
                />
                {errors.username && <p className="mt-1 text-xs text-red-600">{errors.username}</p>}
              </div>


              {editingUser ? null : (
                <div className="min-w-[200px]">
                  <Input
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      if (!editingUser) validateField('password', e.target.value);
                    }}
                    onBlur={(e) => !editingUser && validateField('password', e.target.value)}
                    required={!editingUser}
                    error={errors.password}
                    className={`w-full px-3 py-2 border-2 ${errors.password ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm min-w-0`}
                  />
                  {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                  {!editingUser && !errors.password && formData.password && (
                    <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters with uppercase, lowercase, number, and special character</p>
                  )}
                </div>
              )}
            </div>

            {/* Second Column */}
            <div className="space-y-4">
              <div className="min-w-[200px]">
                <Input
                  label="Last Name"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => {
                    setFormData({ ...formData, lastName: e.target.value });
                    validateField('lastName', e.target.value);
                  }}
                  onBlur={(e) => validateField('lastName', e.target.value)}
                  required
                  error={errors.lastName}
                  className={`w-full px-3 py-2 border-2 ${errors.lastName ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm min-w-0`}
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
              </div>

              <div className="min-w-[200px]">
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    validateField('email', e.target.value);
                  }}
                  onBlur={(e) => validateField('email', e.target.value)}
                  required
                  error={errors.email}
                  className={`w-full px-3 py-2 border-2 ${errors.email ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm min-w-0`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>

              <div className="min-w-[200px]">
                <Input
                  label="Mobile Number"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d+]/g, ''); // Only allow digits and +
                    setFormData({ ...formData, mobile: value });
                    validateField('mobile', value);
                  }}
                  onBlur={(e) => validateField('mobile', e.target.value)}
                  required
                  error={errors.mobile}
                  className={`w-full px-3 py-2 border-2 ${errors.mobile ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm min-w-0`}
                />
                {errors.mobile && <p className="mt-1 text-xs text-red-600">{errors.mobile}</p>}
                {!errors.mobile && formData.mobile && (
                  <p className="mt-1 text-xs text-gray-500">Format: +1234567890 or 1234567890</p>
                )}
              </div>
            </div>
          </div>

          {/* Full-width Role Selector */}
          <div className="max-w-md mx-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className={`w-full px-3 py-2 border-2 ${errors.role ? 'border-red-500' : 'border-slate-200'} rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm`}
              required
            >
              <option value="">Select a role</option>
              <option value="KYC_OFFICER">KYC Officer</option>
              <option value="ADMIN">Admin</option>
            </select>
            {errors.role && <p className="mt-1 text-xs text-red-600">{errors.role}</p>}
          </div>

          {/* Buttons - Centered */}
          <div className="flex gap-3 justify-center pt-4">
            <Button type="button" variant="ghost" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              className='bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white font-semibold px-6'
              isLoading={isSubmitting}
              disabled={
                Object.values(errors).some(error => error) || isSubmitting
              }
            >
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>


      <Modal
        isOpen={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setNewPassword('');
          setResetErrors('');
        }}
        title="Reset Password"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setResetErrors('');
            }}
            className="w-full"
          />

          {resetErrors && (
            <p className="text-sm text-red-600">{resetErrors}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowResetModal(false);
                setNewPassword('');
                setResetErrors('');
              }}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              onClick={handleResetPassword}
              className="bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
            >
              Confirm Reset
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};